import React from 'react';
import {observer} from 'mobx-react'
import Input from '../base-components/Input'
import Col from '../base-components/Col'
import DropDownList, { DataListItem } from '../base-components/DropDownList'
import * as ActionEvent from '../../utils/events/actionEvent'
import {emptyMobxObj} from '../../utils/mobx-utils'
import {addError, BindedComponentError, clearError, parseError} from '../../utils/error-utils'
import * as Validation from '../../utils/validation-utils'
import {toBoolean} from '../../utils/config-utils';
import {BaseUIComponentProps, DomainAttrInUIComponentProps} from './BaseUIComponent';
import { hasRealDomainDataId, IDomainData, IPaymentType, IUIAccountInputDef } from '../../datatypes';

const {MDBInput, MDBBtn, MDBContainer, MDBIcon} = require('mdbreact');

interface AccountInputProps extends BaseUIComponentProps{
    domainAttribute:DomainAttrInUIComponentProps;
    metadata: IUIAccountInputDef;
    paymentTypes:IPaymentType[];
}

interface AccountInputState {
    selectedPaymentType?: IPaymentType,
    namePrefix: string,
    loading: boolean,
    AccountNumber: string,
    AuthToken1: string,
    AuthToken2: string,
    AuthToken3: string,
    AccountToken: string,
};

export default observer(class AccountInput extends React.Component<AccountInputProps & {[key:string]:IDomainData},
                                                                   AccountInputState> {
  private _unmounted: any;

  constructor(props:AccountInputProps) {
    // @ts-ignore
    super(props)
    
    const {paymentTypes} = this.props;
    const domainAttribute = this.props.domainAttribute;

    let parent = this.getParentFromProps();
    // get a payment type selected in parent, or default to 0
    let selectedPaymentType = paymentTypes.find(p => p.PaymentTypeCode === parent.PaymentTypeCode);
    if(!selectedPaymentType){
        if(paymentTypes[0]){
            selectedPaymentType = paymentTypes[0];
        }else{
            selectedPaymentType = {} as IPaymentType;
        }
    } 
    let accountNumber = '';
    let authToken1 = '';
    let authToken2 = '';
    let authToken3 = '';
    let accountToken = '';

    let ptAlreadyPopulated = parent.PaymentTypeCode === selectedPaymentType.PaymentTypeCode &&
                             parent.PaymentTypeId === selectedPaymentType.PaymentTypeId;
    if(ptAlreadyPopulated){
      accountNumber = parent.AccountNumber as string;
      authToken1 = parent.AuthToken1  as string;
      authToken2 = parent.AuthToken2  as string;
      authToken3 = parent.AuthToken3  as string;
      accountToken = parent.AccountToken  as string;
    }
    this.state = {
      selectedPaymentType: selectedPaymentType,
      namePrefix: domainAttribute.namePrefix,
      loading: false,
      AccountNumber: accountNumber,
      AuthToken1: authToken1,
      AuthToken2: authToken2,
      AuthToken3: authToken3,
      AccountToken: accountToken,
    };

    if(!ptAlreadyPopulated){
      this.populatePaymentType(paymentTypes[0]||{});
    }
  }

  selectPaymentType(ptCode:string) {
    let newPt = this.props.paymentTypes.find(pt => pt.PaymentTypeCode === ptCode);
    this.setState({
      selectedPaymentType: newPt
    });
    this.populatePaymentType(newPt||{} as IPaymentType);

  }

  populatePaymentType(newPt:IPaymentType) {
    let parent = this.getParentFromProps();
    if(parent.hasOwnProperty('PaymentTypeId')) {
      parent.PaymentTypeId = newPt.PaymentTypeId;
    }
    if(parent.hasOwnProperty('PaymentTypeCode')) {
      parent.PaymentTypeCode = newPt.PaymentTypeCode;
    }
    if(parent.hasOwnProperty('PaymentTypeBillImageUrl')) {
      parent.PaymentTypeBillImageUrl = newPt.BillImageUrl;
    }
    if(parent.hasOwnProperty('PaymentTypeDescription')) {
      parent.PaymentTypeDescription = newPt.PaymentTypeDescription;
    }
    this.resetLiability();
  }
  componentDidMount() {
    let {formBean}= this.props;
    formBean.validators.push(this.isValid)
  }

  validateField(parent:IDomainData, fieldName:string, label:string|null, enabled:boolean, required:boolean) {
    if(!enabled) {
      return true;
    }
    let valid = true;
    const {formBean, metadata} = this.props;
    if(required) {
        // console.debug(`AccountInput validate`,'field',fieldName,'value', parent[fieldName],'label',label)
      let errMsg = Validation.notEmpty(parent[fieldName], formBean);
      let objectName = parent._edgeName;

      let errorProps:BindedComponentError = {
        error: errMsg||undefined,
        code: 'attribute.empty',
        domainObject: parent,
        attrName: fieldName,
        formBean: formBean,
        objectName: objectName,
        objectId: parent.Id as string,
        metadata: metadata
      }
      if(errMsg) {
        valid = false;
        addError(errorProps);
      }else
        clearError(errorProps)
    }
    return valid;
  }

  isValid = () => {
    const selectedPaymentType = this.state.selectedPaymentType!;
    const accountRequired = toBoolean(selectedPaymentType.AccountRequiredFlag);

    let parent = this.getParentFromProps();
    // console.debug(`AccountInput isValid selectedPaymentType=`, selectedPaymentType);
    // console.debug(`AccountInput isValid parent=`, parent);
    let valid = this.validateField(parent, 'AccountNumber',selectedPaymentType.AccountNumberLabel || 'Account Number', 
                                   true, accountRequired);

    valid = valid && this.validateField(parent, 'PaymentTypeCode', selectedPaymentType.PaymentTypeCode || 'Payment Type Code', 
                                 true, true);
    valid = valid && this.validateField(parent, 'AccountToken',selectedPaymentType.AccountTokenLabel || 'AccountToken Number', 
                                 selectedPaymentType.AccountTokenEnabled, true);
    valid = valid && this.validateField(parent, 'AuthToken1',null, selectedPaymentType.LiabilityToken1Enabled, accountRequired);
    valid = valid && this.validateField(parent, 'AuthToken2',null, selectedPaymentType.LiabilityToken2Enabled, accountRequired);
    valid = valid && this.validateField(parent, 'AuthToken3',null, selectedPaymentType.LiabilityToken3Enabled, accountRequired);

    return valid;
  }
  onChangeInput(propertyName:string, value:string|null) {
    const domainAttribute = this.props.domainAttribute;
    let parent = this.getParentFromProps();
    // @ts-ignore
    this.setState({[propertyName]: value});
    this.resetLiability();
    if(parent.hasOwnProperty(propertyName)) {
      parent[propertyName] = value
    }
    let errorProps:BindedComponentError = {
      domainObject: parent,
      attrName: propertyName,
      formBean: this.props.formBean,
      objectId: (this.props[domainAttribute.objectName] as IDomainData).Id,
    }
    clearError(errorProps);

    //no find button for non liability billers
    if(!this.props.liabilityFileSupported && this.state.selectedPaymentType) {
      let selectedPaymentType = this.state.selectedPaymentType;
      let allFieldsPresent = parent.AccountNumber
        && (!selectedPaymentType.AccountTokenEnabled || parent.AccountToken)
        && (!selectedPaymentType.LiabilityToken1Enabled || parent.AuthToken1)
        && (!selectedPaymentType.LiabilityToken2Enabled || parent.AuthToken2)
        && (!selectedPaymentType.LiabilityToken3Enabled || parent.AuthToken3);
    }
  }

  getParentFromProps() {
    let {domainAttribute} = this.props;
    let parent = this.props[domainAttribute.objectName]; // e.g PaymentHeader object
    return parent;
  }

  isLiabilityDefined() {
    let liability = this.getCtxLiability() as IDomainData;
    return liability && liability.Id && hasRealDomainDataId(liability.Id) && liability.AccountNumber
  }

  getCtxLiability() {
    let {domainAttribute} = this.props;
    let parent = this.getParentFromProps() as IDomainData; // e.g PaymentHeader
    let name = 'LiabilityInfo';
    if (domainAttribute.name !== domainAttribute.objectName) { // ???
      name = domainAttribute.name;
    }
    let ctxLiability = parent[name];
    if(!ctxLiability) return null;
    // @ts-ignore ???
    if (ctxLiability.data) ctxLiability = ctxLiability.data;
    return ctxLiability;
  }

  resetLiability() {
    let ctxLiability = this.getCtxLiability();
    if(ctxLiability){
      emptyMobxObj(ctxLiability);
    }
  }

  async findLiability() {
    if(!this.isValid()){
      return;
    }
    this.setState({loading: true});
    // populate liability and update plugin data
    await ActionEvent.submitRequests(this.props.formBean, ["PaymentFacade.populateLiabilityInfo"]);
    if(!this._unmounted)
      this.setState({loading: false});
  }
  componentWillUnmount() {
    this._unmounted = true;
  }
  retrieveEmptyError(errors: any, fieldName:string) {
    return errors[fieldName] && errors[fieldName].code.endsWith('.empty') ? parseError(errors[fieldName].message, this.props.metadata): null;
  }

  render() {
    const {
      metadata,
      paymentTypes,
      liabilityFileSupported      
    } = this.props;
    const {
      AccountNumber,
      AccountToken,
      namePrefix,
      AuthToken1,
      AuthToken2,
      AuthToken3,
      loading,
    } = this.state;
    const selectedPaymentType = this.state.selectedPaymentType!;
    
    let {title, properties, paymentTypeDisplay, showFind} = metadata;
    let {xs, sm, md, lg} = properties;
    let parent = this.getParentFromProps();
    let errorsObj = parent._errors || {};
    let error = errorsObj.AccountNumber ?  errorsObj.AccountNumber.message: null;
	let errorDropDownList = errorsObj["PaymentTypeCode"] ? errorsObj["PaymentTypeCode"].message : null;
    let authToken1Error = errorsObj.AuthToken1 ? errorsObj.AuthToken1.message : null;
    let authToken2Error = errorsObj.AuthToken2 ? errorsObj.AuthToken2.message : null;
    let authToken3Error = errorsObj.AuthToken3 ? errorsObj.AuthToken3.message : null;
    let isValidated = !loading && this.isLiabilityDefined() && !error;
    
    const accountRequired = toBoolean(selectedPaymentType.AccountRequiredFlag);

    return (
      <Col xs={xs} sm={sm} md={md} lg={lg}>
        <MDBContainer fluid style={{padding:"0px"}} >
          {title && <h3>{title}</h3>}
          {/* {error && <MDBAlert color="danger" >{error}</MDBAlert>} */}
          { paymentTypeDisplay === 'Radio' ?
              paymentTypes.map(pt => (
              <MDBInput
                key={pt.PaymentTypeCode}
                label={pt.PaymentTypeDescription}
                name={`${namePrefix}` + "paymentType"}
                type="radio"
                checked={selectedPaymentType.PaymentTypeCode === pt.PaymentTypeCode}
                className="pt-radio"
                id={`account-pt-selection-${namePrefix}${pt.PaymentTypeCode}`}
                value={pt.PaymentTypeCode}
                onClick={() => this.selectPaymentType(pt.PaymentTypeCode)}
              />
            )): <DropDownList
                  id={namePrefix + 'payment-type-dropdown'}
                  label={"Payment Type"}
                  value={selectedPaymentType.PaymentTypeCode}
                  onChange={(v:any) => this.selectPaymentType(v as string)}
                  dataList={paymentTypes as DataListItem[]}
                  valueName="PaymentTypeCode"
                  valueText="PaymentTypeDescription"
                  error={parseError(errorDropDownList, { label: "Payment Type" })}

              />
          }
            <Input
              label={selectedPaymentType.AccountNumberLabel || "Account Number"}
              name={namePrefix + "AccountNumber"}
              value={AccountNumber}
              error={parseError(error, {label: selectedPaymentType.AccountNumberLabel || "Account Number"})}
              required={accountRequired}
              onChangeHandler={(e) => this.onChangeInput('AccountNumber',e||null)}
            />
            {
              selectedPaymentType.AccountTokenEnabled &&
              <Input
                name={namePrefix + "AccountToken"}
                autoComplete="off"
                value={AccountToken}
                required={true}
                error={this.retrieveEmptyError(errorsObj,'AccountToken')}
                label={selectedPaymentType.AccountTokenLabel || "Account Token"}
                onChangeHandler={(e) => this.onChangeInput('AccountToken',e||null)}/>
            }
            {
              selectedPaymentType.LiabilityToken1Enabled && accountRequired &&
              <Input
                name={namePrefix + "AuthToken1"}
                autoComplete="off"
                value={AuthToken1}
                required={true}
                error={parseError(authToken1Error, {label: selectedPaymentType.AuthToken1Label || "Liability Token 1"})}
                label={selectedPaymentType.AuthToken1Label as string || "Liability Token 1"}
                onChangeHandler={(e) => this.onChangeInput('AuthToken1',e||null)}/>
            }
            {
              selectedPaymentType.LiabilityToken2Enabled && accountRequired &&
              <Input
                name={namePrefix + "AuthToken2"}
                autoComplete="off"
                value={AuthToken2}
                required={true}
                error={parseError(authToken2Error, {label: selectedPaymentType.AuthToken2Label || "Liability Token 2"})}
                label={selectedPaymentType.AuthToken2Label as string || "Liability Token 2"}
                onChangeHandler={(e) => this.onChangeInput('AuthToken2',e||null)}/>
            }
            {
              selectedPaymentType.LiabilityToken3Enabled && accountRequired &&
              <Input
                name={namePrefix + "AuthToken3"}
                autoComplete="off"
                value={AuthToken3}
                required={true}
                label={selectedPaymentType.AuthToken3Label as string || "Liability Token 3"}
                error={parseError(authToken3Error, {label: selectedPaymentType.AuthToken3Label || "Liability Token 3"})}
                onChangeHandler={(e) => this.onChangeInput('AuthToken3',e||null)}/>
            }
            <div style={{display: 'flex', alignItems: 'center'}}>
            {liabilityFileSupported && showFind &&
              <MDBBtn color="secondary" outline disabled={loading || isValidated} size="sm" style={{}} onClick={this.findLiability.bind(this)}>
                <MDBIcon icon="search" style={{"marginRight":"1.5px"}}/>
                Find
              </MDBBtn>
            }
            {liabilityFileSupported && showFind && isValidated &&  <MDBIcon icon="check" className="green-text" />}
            </div>
        </MDBContainer>
      </Col>
    )
  }
})
